import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { GlobalNav } from '../modules/GlobalNav';
import { GlobalNavProps, NavItemType } from '../modules/types';

export interface RenderGlobalNavOrUndefined {
    config: GlobalNavProps;
    featureFlag?: boolean;
}
export const RenderGlobalNavOrUndefined = ({
    config,
}: RenderGlobalNavOrUndefined) => {
    const {
        positionSticky,
        searchBar,
        logo,
        navigation,
        user,
        env,
        intentToggle,
    } = config;
    if (!user.isAuthenticated) {
        return (
            <GlobalNav
                positionSticky={positionSticky}
                searchBar={searchBar}
                logo={logo}
                navigation={navigation}
                user={user}
                env={env}
                intentToggle={intentToggle}
            />
        );
    }
};

export const alternativePath = (path: string, tld: string) => {
    const linkMapByTld: any = {
        'l/sell-tickets/': {
            default: 'l/sell-tickets/',
            '.com': 'organizer/features/sell-tickets/',
            '.ca': 'organizer/features/sell-tickets/',
            '.co.uk': 'organizer/features/sell-tickets/',
            '.nl': 'organizer/features/sell-tickets/',
            '.com.au': 'organizer/features/sell-tickets/',
            '.ie': 'organizer/features/sell-tickets/',
            '.co.nz': 'organizer/features/sell-tickets/',
            '.de': 'organizer/features/sell-tickets/',
            '.it': 'organizer/features/sell-tickets/',
            '.com.br': 'organizer/features/sell-tickets/',
            '.es': 'organizer/features/sell-tickets/',
        },
        'l/conferences/': {
            default: 'l/conferences/',
            '.co.uk': 'organizer/event-type/conferences/',
            '.com': 'organizer/event-type/conferences/',
            '.co.nz': 'organizer/event-type/conferences/',
            '.com.au': 'organizer/event-type/conferences/',
            '.ie': 'organizer/event-type/conferences/',
            '.ca': 'organizer/event-type/conferences/',
        },
        'l/contact-eventbrite-sales/': {
            default: 'l/contact-eventbrite-sales/',
            '.com': 'organizer/contact-sales/',
            '.ca': 'organizer/contact-sales/',
            '.co.uk': 'organizer/contact-sales/',
            '.fr': 'organizer/contact-sales/',
            '.nl': 'organizer/contact-sales/',
            '.com.au': 'organizer/contact-sales/',
            '.hk': 'organizer/contact-sales/',
            '.ie': 'organizer/contact-sales/',
            '.co.nz': 'organizer/contact-sales/',
            '.sg': 'organizer/contact-sales/',
            '.fi': 'organizer/contact-sales/',
            '.ch': 'organizer/contact-sales/',
            '.de': 'organizer/contact-sales/',
            '.it': 'organizer/contact-sales/',
            '.pt': 'organizer/contact-sales/',
            '.com.br': 'organizer/contact-sales/',
            '.es': 'organizer/contact-sales/',
            '.com.ar': 'organizer/contact-sales/',
            '.cl': 'organizer/contact-sales/',
            '.co': 'organizer/contact-sales/',
            '.com.mx': 'organizer/contact-sales/',
            '.com.pe': 'organizer/contact-sales/',
            '.se': 'organizer/contact-sales/',
        },
        'l/eventbrite-ads/': {
            default: 'l/eventbrite-ads/',
            '.com': 'organizer/features/eventbrite-ads/',
            '.ca': 'organizer/features/eventbrite-ads/',
            '.co.uk': 'organizer/features/eventbrite-ads/',
            '.com.au': 'organizer/features/eventbrite-ads/',
        },
        'l/event-payment/': {
            default: 'l/event-payment/',
            '.com': 'organizer/features/event-payment/',
            '.ca': 'organizer/features/event-payment/',
            '.co.uk': 'organizer/features/event-payment/',
            '.com.au': 'organizer/features/event-payment/',
            '.ie': 'organizer/features/event-payment/',
            '.co.nz': 'organizer/features/event-payment/',
        },
        'l/music/': {
            default: 'l/music/',
            '.co.uk': '/organizer/event-industry/music/',
            '.com': '/organizer/event-industry/music/',
            '.co.nz': '/organizer/event-industry/music/',
            '.com.au': '/organizer/event-industry/music/',
            '.ie': '/organizer/event-industry/music/',
            '.ca': '/organizer/event-industry/music/',
            '.sg': '/organizer/event-industry/music/',
            '.de': '/organizer/event-industry/music/',
        },
        'l/npo/': {
            default: 'l/npo/',
            '.co.uk': 'organizer/event-type/npo/',
            '.com': 'organizer/event-type/npo/',
            '.co.nz': 'organizer/event-type/npo/',
            '.com.au': 'organizer/event-type/npo/',
            '.ie': 'organizer/event-type/npo/',
            '.ca': 'organizer/event-type/npo/',
        },
    };
    return linkMapByTld?.[path]?.[tld] || path;
};

export const getSigninOrSignupUrl = (serverUrl?: string) => {
    if (
        !CREATOR_INTENTED_PAGES.some((path) =>
            decodeURIComponent(serverUrl ?? '').includes(path),
        )
    )
        return serverUrl ?? '/signin/';

    if (
        isReferrerMLP(serverUrl) &&
        isUrlCreatorOnboarding(serverUrl) &&
        serverUrl
    ) {
        const mlp_signup_url = new URL(
            serverUrl ?? 'https://www.eventbrite.com',
        );
        mlp_signup_url.searchParams.delete('bust_wagtail_cache');
        mlp_signup_url.searchParams.delete('referrer');
        return mlp_signup_url.toString();
    }

    const AUTH_PATH = serverUrl?.includes('/signup')
        ? '/signin/signup/'
        : '/signin/';
    const url = new URL(serverUrl ?? 'https://www.eventbrite.com');

    url.pathname = AUTH_PATH;
    url.searchParams.set('referrer', '/manage/events/create/');
    url.searchParams.delete('bust_wagtail_cache');
    if (isReferrerPricingPage(serverUrl)) {
        return decodeURIComponent(url.toString());
    }
    return url.toString();
};

export const onEnterKey = (
    event: React.KeyboardEvent,
    callback: () => void,
) => {
    if (event.key === 'Enter') {
        callback();
    }
};

export const isReferrerMLP = (serverUrl?: string) => {
    return MARKETING_LANDING_PAGES_PATHS.some((path) =>
        decodeURIComponent(serverUrl ?? '').includes(path),
    );
};

export const isReferrerPricingPage = (serverUrl?: string) => {
    return serverUrl?.includes('pricing') && isReferrerMLP(serverUrl);
};

export const isUrlCreatorOnboarding = (serverUrl?: string) => {
    return serverUrl?.includes('/creator/onboarding/onboarding-redirect');
};

export const updateQueryStringParameter = (
    url: string,
    key: string,
    value: string,
): string => {
    const queryStart = url.indexOf('?');
    const queryString = queryStart !== -1 ? url.substring(queryStart + 1) : '';
    const baseUrl = queryStart !== -1 ? url.substring(0, queryStart) : url;
    const params = new URLSearchParams(queryString);
    params.set(key, value);
    return baseUrl + '?' + params.toString();
};

export const creatorDesktopItems = (tld: string, desktop?: NavItemType[]) => {
    const creatorDesktop = [...(desktop || [])];
    const contactSalesIndex = creatorDesktop.findIndex(
        (item) => item.heapIdentifier === 'Contact Sales',
    );
    if (contactSalesIndex !== -1) {
        creatorDesktop.splice(contactSalesIndex, 1, {
            path: alternativePath('l/contact-eventbrite-sales/', tld),
            translatedText: gettext('Contact Sales'),
            heapIdentifier: 'Contact Sales',
        });
    }
    return creatorDesktop;
};

const CREATOR_INTENTED_PAGES = ['/l/', '/organizer/', '/boost', '/reconvene'];
const MARKETING_LANDING_PAGES_PATHS = ['/l/', '/organizer/'];
